// Shelfology Custom: announce form submit/validation errors for screen readers (WCAG 4.1.3).
(() => {
  if (window.__customFormErrorLiveRegionInit) return;
  window.__customFormErrorLiveRegionInit = true;

  const ANNOUNCE_DELAY = 50;
  const CLEAR_DELAY = 5000;
  const COLLECT_DELAY = 50;
  const announceTimers = new WeakMap();
  const boundRoots = new WeakSet();
  const pendingInvalid = new WeakMap();

  function cssEscape(value) {
    const text = String(value);
    if (window.CSS?.escape) return CSS.escape(text);
    return text.replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }

  function visibleText(el) {
    if (!(el instanceof Element)) return '';
    if (el.getAttribute('aria-hidden') === 'true') return '';
    if (el.hidden || el.getAttribute('hidden') !== null) return '';
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return '';
    return (el.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function announce(region, message) {
    if (!(region instanceof Element)) return;
    const text = String(message || '').replace(/\s+/g, ' ').trim();
    if (!text) return;

    const previous = announceTimers.get(region);
    if (previous) {
      window.clearTimeout(previous.setTimeoutId);
      window.clearTimeout(previous.clearTimeoutId);
    }

    region.textContent = '';
    const timers = { setTimeoutId: null, clearTimeoutId: null };
    timers.setTimeoutId = window.setTimeout(() => {
      region.textContent = text;
      timers.setTimeoutId = null;
      timers.clearTimeoutId = window.setTimeout(() => {
        if (region.textContent === text) {
          region.textContent = '';
        }
        announceTimers.delete(region);
      }, CLEAR_DELAY);
    }, ANNOUNCE_DELAY);

    announceTimers.set(region, timers);
  }

  function createLiveRegion() {
    const region = document.createElement('p');
    region.className = 'sr-only';
    region.setAttribute('role', 'status');
    region.setAttribute('aria-live', 'polite');
    region.setAttribute('aria-atomic', 'true');
    region.setAttribute('data-form-error-live-region', '');
    return region;
  }

  // Cart shipping-calculator uses lastElementChild as the results alert. Insert
  // before that node so Concept cart.js keeps working.
  function ensureRegion(root) {
    if (!(root instanceof Element)) return null;
    const existing = root.querySelector('[data-form-error-live-region]');
    if (existing) return existing;

    const region = createLiveRegion();
    const last = root.lastElementChild;
    if (last) {
      root.insertBefore(region, last);
    } else {
      root.appendChild(region);
    }
    return region;
  }

  function findRegion(node) {
    if (!(node instanceof Element)) {
      return document.querySelector('[data-form-error-live-region]');
    }

    const scopedRoot =
      node.closest('[data-form-error-root]') ||
      node.closest('form.newsletter-form') ||
      node.closest('form[is="shipping-calculator"]');

    if (scopedRoot) {
      return ensureRegion(scopedRoot);
    }

    return document.querySelector('[data-form-error-live-region]');
  }

  function fieldLabel(field) {
    if (!(field instanceof Element)) return '';
    const id = field.id;
    if (id) {
      const label = document.querySelector(`label[for="${cssEscape(id)}"]`);
      if (label) {
        const copy = label.cloneNode(true);
        copy.querySelectorAll('.sr-only, [aria-hidden="true"]').forEach((el) => el.remove());
        const text = (copy.textContent || '').replace(/\s+/g, ' ').trim();
        if (text) return text;
      }
    }
    return (
      field.getAttribute('aria-label') ||
      field.getAttribute('placeholder') ||
      field.getAttribute('name') ||
      ''
    ).trim();
  }

  function collectInvalidMessages(form) {
    if (!(form instanceof HTMLFormElement)) return '';
    const messages = [];
    form.querySelectorAll(':invalid').forEach((field) => {
      if (!(field instanceof HTMLElement) || field.disabled) return;
      const validity = field.validationMessage ? field.validationMessage.trim() : '';
      if (!validity) return;
      const label = fieldLabel(field);
      messages.push(label ? `${label}: ${validity}` : validity);
    });
    return messages.join('. ');
  }

  function collectAlertMessages(root) {
    if (!(root instanceof Element)) return '';
    const messages = [];
    root.querySelectorAll('.alert--error, [role="alert"]').forEach((el) => {
      if (el.hasAttribute('data-form-error-live-region')) return;
      if (el.hidden || el.getAttribute('hidden') !== null) return;
      const text = visibleText(el);
      if (text) messages.push(text);
    });
    return messages.join('. ');
  }

  function collectDescribedErrors(root) {
    if (!(root instanceof Element)) return '';
    const messages = [];
    const scope = root.shadowRoot ? [root, root.shadowRoot] : [root];

    const visit = (context) => {
      if (!context || !context.querySelectorAll) return;
      context.querySelectorAll('[aria-invalid="true"]').forEach((field) => {
        const describedby = field.getAttribute('aria-describedby');
        if (describedby) {
          describedby.split(/\s+/).forEach((id) => {
            if (!id) return;
            const described =
              context.getElementById?.(id) ||
              context.querySelector?.(`#${cssEscape(id)}`) ||
              document.getElementById(id);
            const text = visibleText(described);
            if (text) messages.push(text);
          });
        }
        if (field instanceof HTMLElement && field.validationMessage) {
          const label = fieldLabel(field);
          const validity = field.validationMessage.trim();
          messages.push(label ? `${label}: ${validity}` : validity);
        }
      });
      context.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) visit(el.shadowRoot);
      });
    };

    scope.forEach(visit);
    return [...new Set(messages.filter(Boolean))].join('. ');
  }

  function collectGenericErrorText(root) {
    if (!(root instanceof Element)) return '';
    const messages = [];
    const visit = (context) => {
      if (!context?.querySelectorAll) return;
      context.querySelectorAll('[data-testid*="error" i], [id*="error" i], [id*="Error" i]').forEach((el) => {
        if (el.hasAttribute('data-form-error-live-region')) return;
        const text = visibleText(el);
        if (text) messages.push(text);
      });
      context.querySelectorAll('*').forEach((el) => {
        if (el.shadowRoot) visit(el.shadowRoot);
      });
    };
    visit(root);
    return [...new Set(messages.filter(Boolean))].join('. ');
  }

  function errorMessageFor(root) {
    return (
      collectAlertMessages(root) ||
      collectDescribedErrors(root) ||
      collectGenericErrorText(root) ||
      (root instanceof HTMLFormElement ? collectInvalidMessages(root) : '')
    );
  }

  function announceFrom(root) {
    const message = errorMessageFor(root);
    const region = findRegion(root);
    if (message && region) announce(region, message);
  }

  function isWatchedForm(form) {
    if (!(form instanceof HTMLFormElement)) return false;
    if (form.classList.contains('newsletter-form')) return true;
    if (form.getAttribute('is') === 'shipping-calculator') return true;
    if (form.closest('[data-form-error-root]')) return true;
    return false;
  }

  function isLiveRegionNode(node) {
    const el = node instanceof Element ? node : node?.parentElement;
    return Boolean(el?.closest?.('[data-form-error-live-region]'));
  }

  function bindDynamicResults(root) {
    if (!(root instanceof Element) || boundRoots.has(root)) return;
    boundRoots.add(root);
    ensureRegion(root);

    let collectTimer = 0;
    const schedule = () => {
      window.clearTimeout(collectTimer);
      collectTimer = window.setTimeout(() => announceFrom(root), COLLECT_DELAY);
    };

    const observer = new MutationObserver((records) => {
      const relevant = records.some((record) => {
        if (isLiveRegionNode(record.target)) return false;
        if ([...record.addedNodes].every((node) => isLiveRegionNode(node)) && record.addedNodes.length) {
          return false;
        }
        return true;
      });
      if (relevant) schedule();
    });
    observer.observe(root, {
      subtree: true,
      childList: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['hidden', 'aria-invalid', 'class'],
    });

    window.setTimeout(() => announceFrom(root), ANNOUNCE_DELAY);
  }

  function bindAll(scope) {
    const root = scope instanceof Element || scope instanceof Document ? scope : document;
    root.querySelectorAll?.('form.newsletter-form, form[is="shipping-calculator"], [data-form-error-root]').forEach(bindDynamicResults);
    if (root instanceof Element && root.matches?.('form.newsletter-form, form[is="shipping-calculator"], [data-form-error-root]')) {
      bindDynamicResults(root);
    }
  }

  document.addEventListener(
    'invalid',
    (event) => {
      const field = event.target;
      if (!(field instanceof HTMLElement)) return;
      const form = field.form || field.closest('form');
      if (!isWatchedForm(form)) return;

      window.clearTimeout(pendingInvalid.get(form));
      pendingInvalid.set(
        form,
        window.setTimeout(() => {
          pendingInvalid.delete(form);
          const message = collectInvalidMessages(form) || errorMessageFor(form);
          const region = findRegion(form);
          if (message && region) announce(region, message);
        }, 0)
      );
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!isWatchedForm(form)) return;
      if (form instanceof HTMLFormElement && !form.checkValidity()) {
        const message = collectInvalidMessages(form);
        const region = findRegion(form);
        if (message && region) announce(region, message);
      }
    },
    true
  );

  bindAll(document);

  const documentObserver = new MutationObserver((records) => {
    records.forEach((record) => {
      record.addedNodes.forEach((node) => {
        if (node instanceof Element) bindAll(node);
      });
    });
  });
  documentObserver.observe(document.documentElement, { subtree: true, childList: true });
})();
